import React, { useState, useEffect } from 'react';
import { Box, Typography, Popper, ClickAwayListener, Paper } from '@mui/material';
import { AuthUser } from '../types';
import { APP_DISPLAY_NAME } from '../config/brand';

const APP_VERSION = process.env.REACT_APP_VERSION ?? '0.1.0';

const THEME_PREF_KEY = 'chaavi-theme-preference';

const F = {
  border: '#d6cfc0',
  text: '#3a2e1e',
  accent: '#c0873a',
  chipBg: '#fdf0e0',
  chipText: '#b07030',
  heroBg: '#fdf8f2',
  heroRule: '#ede8dc',
  iconMuted: '#a89880',
  danger: '#b03a2a',
  dangerIcon: '#c0503a',
  footerMuted: '#c0b8a8',
  logoutText: '#7a6a55',
  logoutIcon: '#b0a090',
  rowHover: '#fdf9f4',
  sectionRule: '#f0ebe0',
};

const iconWrapSx = {
  width: 16,
  height: 16,
  flexShrink: 0,
  color: F.iconMuted,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { width: 16, height: 16, stroke: 'currentColor', fill: 'none', strokeWidth: 1.6 },
};

export interface ChaaviClientProfileMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  user: AuthUser;
  /** Shown next to "My orders" (e.g. unread order-related notifications). */
  ordersNewCount: number;
  onMyOrders: () => void;
  onChat: () => void;
  onMyRatings: () => void;
  onEditProfile: () => void;
  onChangePassword: () => void;
  onDeleteAccount: () => void;
  onLogout: () => void;
}

const IconOrders: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const IconChat: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconStar: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const IconUser: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const IconLock: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const IconTrash: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const IconLogout: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const IconSun: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const IconMoon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
  </svg>
);

const ChaaviClientProfileMenu: React.FC<ChaaviClientProfileMenuProps> = ({
  open,
  anchorEl,
  onClose,
  user,
  ordersNewCount,
  onMyOrders,
  onChat,
  onMyRatings,
  onEditProfile,
  onChangePassword,
  onDeleteAccount,
  onLogout,
}) => {
  const [themePref, setThemePref] = useState<'light' | 'dark'>(() => {
    const s = localStorage.getItem(THEME_PREF_KEY);
    return s === 'dark' ? 'dark' : 'light';
  });

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_PREF_KEY && (e.newValue === 'light' || e.newValue === 'dark')) {
        setThemePref(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setThemePreference = (mode: 'light' | 'dark') => {
    localStorage.setItem(THEME_PREF_KEY, mode);
    setThemePref(mode);
    window.dispatchEvent(new CustomEvent('chaavi-theme-preference', { detail: mode }));
  };

  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || 'Client';
  const initial = (user.firstName?.charAt(0) || user.email?.charAt(0) || 'C').toUpperCase();

  const menuRow = (
    onClick: () => void,
    label: string,
    Icon: React.FC,
    opts?: { badge?: string; danger?: boolean; logout?: boolean }
  ) => (
    <Box
      component="button"
      type="button"
      onClick={() => {
        onClick();
        onClose();
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.375,
        py: 1.25,
        px: 2,
        cursor: 'pointer',
        transition: 'background 0.1s',
        color: opts?.danger ? F.danger : opts?.logout ? F.logoutText : F.text,
        fontSize: '0.8125rem',
        fontFamily: '"Jost", "Roboto", "Helvetica Neue", Arial, sans-serif',
        border: 'none',
        background: 'none',
        width: '100%',
        textAlign: 'left',
        boxSizing: 'border-box',
        '&:hover': {
          bgcolor: opts?.danger ? '#fff5f3' : F.rowHover,
        },
        '&:hover .chaavi-profile-menu-icon': {
          color: opts?.danger ? F.danger : opts?.logout ? F.logoutIcon : F.accent,
        },
      }}
    >
      <Box
        className="chaavi-profile-menu-icon"
        sx={{
          ...iconWrapSx,
          color: opts?.danger ? F.dangerIcon : opts?.logout ? F.logoutIcon : F.iconMuted,
        }}
      >
        <Icon />
      </Box>
      <Typography component="span" sx={{ flex: 1, fontSize: 'inherit', color: 'inherit' }}>
        {label}
      </Typography>
      {opts?.badge ? (
        <Typography
          component="span"
          sx={{
            fontSize: '0.625rem',
            bgcolor: F.chipBg,
            color: F.chipText,
            px: 0.75,
            py: 0.25,
            borderRadius: '8px',
            fontFamily: '"Jost", sans-serif',
            fontWeight: 500,
          }}
        >
          {opts.badge}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      sx={{ zIndex: theme => theme.zIndex.modal }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={0}
          sx={{
            width: 272,
            borderRadius: '14px',
            border: `0.5px solid ${F.border}`,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Box
            sx={{
              px: 2.25,
              py: 2,
              pb: 1.75,
              background: F.heroBg,
              borderBottom: `0.5px solid ${F.heroRule}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.625,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: F.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.125rem',
                fontWeight: 500,
                color: '#fff',
                flexShrink: 0,
                border: '2px solid #e8d5b7',
                fontFamily: '"Jost", "Roboto", sans-serif',
              }}
            >
              {initial}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: F.text,
                  fontFamily: '"Jost", "Roboto", sans-serif',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: F.iconMuted,
                  fontFamily: '"Jost", "Roboto", sans-serif',
                  mt: 0.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user.email}
              </Typography>
              <Typography
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  mt: 0.625,
                  bgcolor: F.chipBg,
                  color: F.chipText,
                  fontSize: '0.625rem',
                  fontWeight: 500,
                  px: 1,
                  py: 0.25,
                  borderRadius: '10px',
                  fontFamily: '"Jost", sans-serif',
                  letterSpacing: '0.04em',
                }}
              >
                {user.userType}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ py: 0.75 }}>
            {menuRow(
              onMyOrders,
              'My orders',
              IconOrders,
              ordersNewCount > 0
                ? { badge: `${ordersNewCount} new` }
                : undefined
            )}
            {menuRow(onChat, 'Chat', IconChat)}
            {menuRow(onMyRatings, 'My ratings', IconStar)}
          </Box>

          <Box sx={{ height: '0.5px', bgcolor: F.sectionRule, my: 0.5 }} />

          <Box sx={{ py: 0.75 }}>
            {menuRow(onEditProfile, 'Edit profile', IconUser)}
            {menuRow(onChangePassword, 'Change password', IconLock)}
          </Box>

          <Box sx={{ height: '0.5px', bgcolor: F.sectionRule, my: 0.5 }} />

          <Box sx={{ py: 0.75 }}>
            {menuRow(onDeleteAccount, 'Delete account', IconTrash, { danger: true })}
            {menuRow(onLogout, 'Logout', IconLogout, { logout: true })}
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.25,
              pb: 1.5,
              borderTop: `0.5px solid ${F.sectionRule}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.6875rem',
                color: F.footerMuted,
                fontFamily: '"Jost", "Roboto", sans-serif',
              }}
            >
              {APP_DISPLAY_NAME.toLowerCase()} v{APP_VERSION}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Box
                component="button"
                type="button"
                title="Light mode"
                onClick={() => setThemePreference('light')}
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '6px',
                  border: `0.5px solid ${F.border}`,
                  bgcolor: themePref === 'light' ? '#f5f0e8' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { bgcolor: '#f5f0e8' },
                  '& svg': { width: 13, height: 13, stroke: F.iconMuted, fill: 'none', strokeWidth: 1.6 },
                }}
              >
                <IconSun />
              </Box>
              <Box
                component="button"
                type="button"
                title="Dark mode"
                onClick={() => setThemePreference('dark')}
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '6px',
                  border: `0.5px solid ${F.border}`,
                  bgcolor: themePref === 'dark' ? '#f5f0e8' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { bgcolor: '#f5f0e8' },
                  '& svg': { width: 13, height: 13, stroke: F.iconMuted, fill: 'none', strokeWidth: 1.6 },
                }}
              >
                <IconMoon />
              </Box>
            </Box>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default ChaaviClientProfileMenu;
